import React from "react";

export default function Refunds() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Refund Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          This Refund Policy sets out the terms under which V CAN RESOURCES LIMITED ("we", "us", "our") provides refunds for the VSENDS platform. By purchasing a subscription to the Service, you agree to the terms of this policy.
        </p>
        <p className="text-gray-700 mb-4">
          We want you to be satisfied with our Service. If you are not, we encourage you to contact our support team at support@vsends.com so we can try to resolve any issues.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Subscription Refunds</h2>
        <p className="text-gray-700 mb-4">
          All subscription fees are billed in advance on a monthly or annual basis, depending on the plan you select. Subscription fees are generally non-refundable, except as described in this policy.
        </p>
        <p className="text-gray-700 mb-4">
          If you cancel your subscription, you will continue to have access to the Service until the end of your current billing period. No partial refunds will be provided for the unused portion of a billing period.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. 14-Day Money-Back Guarantee</h2>
        <p className="text-gray-700 mb-4">
          We offer a 14-day money-back guarantee for new subscribers. If you are not satisfied with the Service within the first 14 days of your initial subscription, you may request a full refund.
        </p>
        <p className="text-gray-700 mb-4">
          To claim the money-back guarantee, you must:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Request the refund within 14 calendar days of your initial purchase.</li>
          <li>Have used the Service in good faith and in accordance with our Terms of Service.</li>
          <li>Contact our support team at support@vsends.com with your Account details and reason for the refund request.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          This guarantee applies only to your first subscription purchase and is not available for subsequent renewals or upgrades.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Refunds for Service Interruptions</h2>
        <p className="text-gray-700 mb-4">
          If the Service experiences a significant and prolonged outage or interruption that materially affects your ability to use the Service, you may be eligible for a refund or service credit.
        </p>
        <p className="text-gray-700 mb-4">
          To be eligible, the interruption must:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Be caused by our infrastructure or systems (not by third-party services or your own configuration).</li>
          <li>Last for more than 24 consecutive hours.</li>
          <li>Be reported to us within 7 days of the interruption.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Refunds or credits for service interruptions will be calculated on a pro-rata basis for the affected period and are at our sole discretion.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Refunds for Billing Errors</h2>
        <p className="text-gray-700 mb-4">
          If you believe you have been charged incorrectly, please contact us at support@vsends.com within 30 days of the charge. We will investigate the matter and, if a billing error is confirmed, issue a refund or credit for the incorrect amount.
        </p>
        <p className="text-gray-700 mb-4">
          Common billing errors include duplicate charges, charges for cancelled subscriptions, and charges at incorrect rates. We will resolve billing disputes promptly and fairly.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Non-Refundable Items</h2>
        <p className="text-gray-700 mb-4">
          The following are not eligible for refunds:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Subscription fees after the 14-day money-back guarantee period has expired.</li>
          <li>Unused portions of subscription periods after cancellation.</li>
          <li>Accounts terminated for violation of our Terms of Service or Acceptable Use Policy.</li>
          <li>Overage charges for usage beyond your plan limits.</li>
          <li>Third-party fees or charges (e.g., payment processing fees, bank charges).</li>
          <li>Custom development or professional services fees.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Refund Process</h2>
        <p className="text-gray-700 mb-4">
          To request a refund, please contact us at support@vsends.com with the following information:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Your Account email address.</li>
          <li>The date and amount of the charge you are requesting a refund for.</li>
          <li>The reason for your refund request.</li>
          <li>Any supporting documentation (e.g., screenshots, error messages).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We will review your request and respond within 10 business days. If approved, refunds will be processed to the original payment method within 14 business days.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Consumer Rights</h2>
        <p className="text-gray-700 mb-4">
          Nothing in this Refund Policy affects your statutory rights as a consumer. Under the UK Consumer Rights Act 2015, you have certain rights if the Service is not provided with reasonable care and skill, is not as described, or is not fit for purpose.
        </p>
        <p className="text-gray-700 mb-4">
          If you are a consumer based in the UK or the EU, you may also have rights under the Consumer Contracts Regulations 2013, which provide a 14-day cooling-off period for distance contracts. However, by using the Service, you expressly consent to the immediate performance of the Service and acknowledge that you lose your right to cancel once the Service has begun.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Changes to This Policy</h2>
        <p className="text-gray-700 mb-4">
          We may update this Refund Policy from time to time. Changes will be effective upon posting to our website with an updated "Last Updated" date. Your continued use of the Service after changes take effect constitutes your acceptance of the updated policy.
        </p>
        <p className="text-gray-700 mb-4">
          For questions about this Refund Policy or to request a refund, please contact us at support@vsends.com or vg@vcanresources.com.
        </p>
      </main>
    </div>
  );
}
